import { t } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ActionKind } from "../../types/Action";
import { OperatorSubStateKind } from "../../types/Operator";
import { Order, PaymentMethod } from "../../types/Order";
import { Button } from "../Button";
import DateRangePicker from "../DateRangePicker";
import { usePriceFormat } from "../PriceFormatProvider";
import { requireSubState } from "../state/Common";
import {
    RootStateKind,
    useRootAndSubState,
    useRootState
} from "../state/StateProvider";
import { chevronLeft, chevronRight } from "../svg";

export const orderListPageSize = 8;

export const OrderList = () => {
    const { t, i18n } = useTranslation();
    const { state, dispatch } = useRootAndSubState(
        RootStateKind.Operator,
        OperatorSubStateKind.Orders
    );

    const { orders, totalCount, dateRange } = state;

    const onPrintOrder = async (orderId: string) => {
        await dispatch({ kind: ActionKind.PrintReceipt, orderId });
    };

    return (
        <div className="m-4 flex h-full flex-col gap-4 pb-16">
            <div className="text-3xl font-semibold text-primary-900">
                {t("orderList.title")}
            </div>
            <DateRangePicker
                language={i18n.language}
                fromDate={dateRange.from}
                toDate={dateRange.to}
                onChange={({ from, to }) =>
                    dispatch({
                        kind: ActionKind.LoadMoreOrders,
                        dateRange: {
                            from,
                            to
                        },
                        offset: 0
                    })
                }
            />
            {orders.length > 0 && (
                <div className="flex h-full flex-col items-center justify-between">
                    <ol className="mb-4 flex w-full flex-[1_0px] flex-col items-center gap-2 overflow-y-auto">
                        {orders.map((order) => (
                            <OrderItem
                                key={order.id}
                                order={order}
                                onPrintOrder={onPrintOrder}
                            />
                        ))}
                    </ol>
                    <OrderPagination count={totalCount} />
                </div>
            )}
        </div>
    );
};

export const OperatorModeBackButton = () => {
    const { t } = useTranslation();
    const { dispatch, state } = useRootState(RootStateKind.Operator);

    const kind =
        state.subState.kind === OperatorSubStateKind.Idle
            ? ActionKind.Reset
            : ActionKind.BackToOperatorModeMenu;

    return (
        <Button variant="secondary" onClick={() => dispatch({ kind })}>
            {t("back")}
        </Button>
    );
};

const Timestamp = ({ timestamp }: { timestamp: Date }) => {
    return (
        <div className="font-semibold">
            {timestamp.toLocaleTimeString([], { timeStyle: "short" })}
        </div>
    );
};

const MaskedPaymentMethod = ({
    className,
    lastCardDigits
}: {
    className?: string;
    lastCardDigits: PaymentMethod["lastDigits"];
}) => {
    if (!lastCardDigits) {
        return <div className={className}>----</div>;
    }

    const isShortVersion = lastCardDigits.length === 4;
    const digits = isShortVersion
        ? lastCardDigits
        : lastCardDigits.slice(
              lastCardDigits.length - 4,
              lastCardDigits.length
          );

    return (
        <div className={`whitespace-nowrap ${className}`}>
            **** **** **** {digits}
        </div>
    );
};

const Total = ({ price }: { price: Order["totalPrice"] }) => {
    const format = usePriceFormat();

    return <div className="font-semibold">{format(price)}</div>;
};

const PrintReceiptButton = ({
    id,
    onPrintOrder
}: {
    id: Order["id"];
    onPrintOrder: (id: string) => void;
}) => {
    return (
        <Button variant="secondary" onClick={() => onPrintOrder(id)}>
            {t("printReceipt")}
        </Button>
    );
};

const OrderDetailsButton = ({
    isOpen,
    toggle
}: {
    isOpen: boolean;
    toggle: () => void;
}) => {
    const { t } = useTranslation();
    const label = `${t("orderList.details")} ${isOpen ? "▾" : "▸"}`;

    return (
        <Button
            onClick={() => toggle()}
            className="whitespace-nowrap p-0 font-semibold text-primary-500"
        >
            {label}
        </Button>
    );
};

const OrderItem = ({
    order,
    onPrintOrder
}: {
    order: Order;
    onPrintOrder: (id: string) => void;
}) => {
    const [isOrderDetailsOpen, setOrderDetailsOpen] = useState<boolean>(false);
    const format = usePriceFormat();

    return (
        <li className="w-full text-sm">
            <div className="flex items-center justify-between gap-4 rounded bg-gray-100 p-4">
                <div className="grid w-64 grid-cols-4">
                    <Timestamp timestamp={new Date(order.orderCreatedAt)} />
                    {order.paymentMethod && (
                        <MaskedPaymentMethod
                            className="col-span-2"
                            lastCardDigits={order.paymentMethod.lastDigits}
                        />
                    )}
                    <Total price={order.totalPrice} />
                </div>

                <div className="flex items-center gap-4">
                    <PrintReceiptButton
                        id={order.id}
                        onPrintOrder={onPrintOrder}
                    />
                    <OrderDetailsButton
                        isOpen={isOrderDetailsOpen}
                        toggle={() => setOrderDetailsOpen(!isOrderDetailsOpen)}
                    />
                </div>
            </div>
            {isOrderDetailsOpen && (
                <div className="bg-primary-100 p-4 text-primary-400">
                    {order.positions.map((p, index) => {
                        switch (p.kind) {
                            case "single":
                                return (
                                    <span
                                        key={index}
                                        className="flex justify-between"
                                    >
                                        {p.quantity}x {p.name}
                                        <span>{format(p.price)}</span>
                                    </span>
                                );
                            case "group":
                                return (
                                    <div key={index}>
                                        <span className="flex justify-between">
                                            {p.quantity}x {p.name}
                                            <span>{format(p.price)}</span>
                                        </span>
                                        {p.lineItems.map((li, i) => (
                                            <span
                                                key={`li-${i}`}
                                                className="flex justify-between pl-4"
                                            >
                                                {li.quantity}x {li.name}
                                                <span>—</span>
                                            </span>
                                        ))}
                                    </div>
                                );
                            default:
                                return p satisfies never;
                        }
                    })}
                </div>
            )}
        </li>
    );
};

const OrderPagination = ({ count }: { count: number }) => {
    const { state, dispatch } = useRootState(RootStateKind.Operator);

    const { offset, dateRange } = requireSubState(
        state,
        RootStateKind.Operator,
        OperatorSubStateKind.Orders
    );

    const currentPage = Math.floor(offset / orderListPageSize);

    const loadPage = (page: number) =>
        dispatch({
            kind: ActionKind.LoadMoreOrders,
            dateRange,
            offset: page * orderListPageSize
        });

    const loadNextPage = () => loadPage(currentPage + 1);
    const loadPreviousPage = () => loadPage(currentPage - 1);

    const pages = [...Array(Math.ceil(count / orderListPageSize)).keys()];
    const isFirstPage = currentPage === 0;
    const isLastPage = currentPage === pages[pages.length - 1];

    return (
        <div className="flex w-fit items-center justify-between gap-3.5">
            <Button
                variant="default"
                className="border-none"
                disabled={isFirstPage}
                onClick={loadPreviousPage}
            >
                {chevronLeft}
            </Button>
            {pages.map((page) => (
                <Button
                    variant={currentPage === page ? "primary" : "default"}
                    key={page}
                    onClick={() => loadPage(page)}
                    className="flex h-8 w-8 items-center justify-center p-6"
                >
                    {page + 1}
                </Button>
            ))}
            <Button
                variant="default"
                className="border-none"
                disabled={isLastPage}
                onClick={loadNextPage}
            >
                {chevronRight}
            </Button>
        </div>
    );
};
